import { useState } from 'react';
import { useNavigate } from 'react-router';
import { LightbulbIcon, CheckCircle2Icon, XCircleIcon, ExternalLinkIcon, ClockIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useListCategorySuggestions,
  useApproveCategorySuggestion,
  useRejectCategorySuggestion,
  useGetWorkspaceAssetTypes,
  getListCategorySuggestionsQueryKey,
  getGetCivicCategoriesQueryKey
} from '@cdf/api-client-react';
import type { CategorySuggestion } from '@cdf/api-client-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDateTime } from '@/lib/report-helpers';

const STATUS_META: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  pending: { label: 'Pending', variant: 'secondary' },
  approved: { label: 'Approved', variant: 'default' },
  rejected: { label: 'Rejected', variant: 'outline' }
};

export function SuggestionsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useListCategorySuggestions();
  const { data: assetTypesData } = useGetWorkspaceAssetTypes();
  const approveMutation = useApproveCategorySuggestion();
  const rejectMutation = useRejectCategorySuggestion();

  const [approveTarget, setApproveTarget] = useState<CategorySuggestion | null>(null);
  const [assetTypeId, setAssetTypeId] = useState<string>('none');

  const suggestions = data?.suggestions ?? [];
  const assetTypes = assetTypesData?.assetTypes ?? [];

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: getListCategorySuggestionsQueryKey() });
    qc.invalidateQueries({ queryKey: getGetCivicCategoriesQueryKey() });
  }

  function handleApprove() {
    if (!approveTarget) return;
    const body = assetTypeId === 'none' ? {} : { assetTypeId };
    approveMutation.mutate(
      { suggestionId: approveTarget.id, data: body },
      {
        onSuccess: () => {
          setApproveTarget(null);
          setAssetTypeId('none');
          invalidateAll();
        }
      }
    );
  }

  function handleReject(suggestion: CategorySuggestion) {
    rejectMutation.mutate(
      { suggestionId: suggestion.id },
      { onSuccess: () => invalidateAll() }
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Type Suggestions"
        description="Review custom issue types submitted by reporters. Approved types join the shared category list."
        icon={LightbulbIcon}
      />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : suggestions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <CheckCircle2Icon className="mb-3 h-10 w-10 text-muted-foreground" />
            <p className="text-lg font-medium">No pending suggestions</p>
            <p className="text-sm text-muted-foreground">
              Custom issue types submitted by reporters will appear here for review.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {suggestions.map((s) => (
            <Card key={s.id}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                <div className="space-y-1">
                  <CardTitle className="text-base">
                    {s.label}
                  </CardTitle>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ClockIcon className="h-3.5 w-3.5" />
                    {formatDateTime(s.createdAt)}
                    {s.report && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 h-6 gap-1 px-2"
                        onClick={() => navigate(`/workspace/reports/${s.report!.id}`)}
                      >
                        <ExternalLinkIcon className="h-3 w-3" />
                        View report
                      </Button>
                    )}
                  </div>
                </div>
                <Badge variant={STATUS_META[s.status]?.variant ?? 'secondary'}>
                  {STATUS_META[s.status]?.label ?? s.status}
                </Badge>
              </CardHeader>
              {s.status === 'pending' && (
                <CardContent className="flex justify-end gap-2 pt-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleReject(s)}
                    disabled={rejectMutation.isPending}
                  >
                    <XCircleIcon className="mr-1 h-4 w-4" />
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setApproveTarget(s);
                      setAssetTypeId('none');
                    }}
                  >
                    <CheckCircle2Icon className="mr-1 h-4 w-4" />
                    Approve
                  </Button>
                </CardContent>
              )}
              {s.status === 'approved' && s.reviewedByStaffName && (
                <CardContent className="pt-0 text-sm text-muted-foreground">
                  Approved by {s.reviewedByStaffName}
                  {s.reviewedAt && ` on ${formatDateTime(s.reviewedAt)}`}
                </CardContent>
              )}
              {s.status === 'rejected' && s.reviewedByStaffName && (
                <CardContent className="pt-0 text-sm text-muted-foreground">
                  Rejected by {s.reviewedByStaffName}
                  {s.reviewedAt && ` on ${formatDateTime(s.reviewedAt)}`}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!approveTarget} onOpenChange={(open) => !open && setApproveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve "{approveTarget?.label}"</DialogTitle>
            <DialogDescription>
              This will create a new shared category from the suggestion label. You can optionally
              map it to an asset type for routing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <label className="text-sm font-medium">Asset type mapping (optional)</label>
            <Select value={assetTypeId} onValueChange={setAssetTypeId}>
              <SelectTrigger>
                <SelectValue placeholder="No asset type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No asset type</SelectItem>
                {assetTypes.map((at) => (
                  <SelectItem key={at.id} value={at.id ?? ''}>
                    {at.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Without an asset type, reports using this category will route to the council of the
              containing region.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={approveMutation.isPending}>
              {approveMutation.isPending ? 'Approving…' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
